import { ExerciseManager } from '@pelckmans/business-components/components/exercise-manager';
import {
  ExercisePhase,
  ExerciseResult,
} from '@pelckmans/business-components/components/exercise-manager/exercise-types';
import { useContext } from 'react';
import getAccessToken from '../../../api/BearerToken';
import ModalContext from '../../../contexts/ModalContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { useTranslation } from 'react-i18next';
import { EduhintMedialink, EdumaticMedialink, isMedialinkStudentAssignment, Medialink } from '../_api/MedialinksApi';
import { Module } from '../_api/ModuleApi';
import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';

const tokenAccessor = {
  tokenGetter: getAccessToken,
};

const getTranslations = (t: ReturnType<typeof useTranslation>['t']) => ({
  exerciseDialog: {
    buttons: {
      pause: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_DIALOG.BUTTONS.PAUSE'),
    },
    loading: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_DIALOG.LOADING'),
    calculating: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_DIALOG.CALCULATING'),
  },
  exerciseRestartDialog: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.TITLE'),
    message: {
      p1: {
        restartable: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.MESSAGE.P1.RESTARTABLE'),
        nonRestartable: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.MESSAGE.P1.NON_RESTARTABLE'),
      },
    },
    scorePrefix: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.SCORE_PREFIX'),
    result: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.RESULT'),
    buttons: {
      restart: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.BUTTONS.RESTART'),
      cancel: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.BUTTONS.CANCEL'),
      review: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESTART_DIALOG.BUTTONS.REVIEW'),
    },
  },
  exerciseResumeDialog: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.TITLE'),
    message: {
      p1: {
        restartable: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.MESSAGE.P1.RESTARTABLE'),
        nonRestartable: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.MESSAGE.P1.NON_RESTARTABLE'),
      },
      p2: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.MESSAGE.P2'),
    },
    scorePrefix: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.SCORE_PREFIX'),
    result: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.RESULT'),
    buttons: {
      resume: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.BUTTONS.RESUME'),
      restart: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.BUTTONS.RESTART'),
      cancel: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESUME_DIALOG.BUTTONS.CANCEL'),
    },
  },
  exerciseResultState: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.TITLE'),
    buttons: {
      restart: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.BUTTONS.RESTART'),
      close: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.BUTTONS.CLOSE'),
      review: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.BUTTONS.REVIEW'),
    },
    messages: {
      veryGood: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.MESSAGES.VERY_GOOD'),
      wellDone: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.MESSAGES.WELL_DONE'),
      bad: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.MESSAGES.BAD'),
      showScore: {
        result: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.MESSAGES.SHOW_SCORE.RESULT'),
        perfectResult: t(
          'BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_RESULT_STATE.MESSAGES.SHOW_SCORE.PERFECT_RESULT',
        ),
      },
    },
  },
  exercisePauseDialog: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSE_DIALOG.TITLE'),
    message: {
      p1: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSE_DIALOG.MESSAGE.P1'),
      p2: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSE_DIALOG.MESSAGE.P2'),
    },
    buttons: {
      pause: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSE_DIALOG.BUTTONS.PAUSE'),
      continue: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSE_DIALOG.BUTTONS.CONTINUE'),
    },
  },
  assignmentExpiredDialog: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_EXPIRED_DIALOG.TITLE'),
    message: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_EXPIRED_DIALOG.MESSAGE'),
    buttons: {
      close: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_EXPIRED_DIALOG.BUTTONS.CLOSE'),
    },
  },
  exercisePausedState: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.TITLE'),
    buttons: {
      close: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.BUTTONS.CLOSE'),
    },
    messages: {
      incomplete: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.MESSAGES.INCOMPLETE'),
      veryGood: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.MESSAGES.VERY_GOOD'),
      wellDone: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.MESSAGES.WELL_DONE'),
      bad: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.MESSAGES.BAD'),
    },
    showScore: {
      title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_PAUSED_STATE.SHOW_SCORE.TITLE'),
    },
  },
  exerciseFailedState: {
    message: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_FAILED_STATE.MESSAGE'),
    buttons: {
      close: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_FAILED_STATE.BUTTONS.CLOSE'),
      retry: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.EXERCISE_FAILED_STATE.BUTTONS.RETRY'),
    },
  },
  closeDialog: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.CLOSE_DIALOG'),
  loading: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.LOADING'),
  generalNode: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.GENERAL_NODE'),
  serviceNotAvailable: {
    title: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.TITLE'),
    p1: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.P1'),
    p2: t('BUSINESS_COMPONENTS.EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.P2'),
  },
  progressionBar: {
    pending: t('BUSINESS_COMPONENTS.PROGRESSION_BAR.PENDING'),
  },
});

const getAssignmentId = (medialink: Medialink) => {
  if (!isMedialinkStudentAssignment(medialink)) return undefined;

  return medialink.assignments.find(x => x.current)?.id;
};

type Props = {
  id: string;
  medialink: EduhintMedialink | EdumaticMedialink;
  module: Module;
  onSessionPaused: (result: ExerciseResult) => void;
  onSessionFinished: (result: ExerciseResult) => void;
  initialPhase?: ExercisePhase;
  onClose?: () => void;
};

export default function ExerciseModal({
  id,
  medialink,
  module,
  onSessionPaused,
  onSessionFinished,
  initialPhase,
  onClose,
}: Props) {
  const { closeModal } = useContext(ModalContext);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const handleError = useThrowAsyncError();

  const handleClose = () => {
    closeModal(id);
    if (onClose) onClose();
  };

  return (
    <ExerciseManager
      onAssignmentExpired={onClose}
      language={language as SupportedUserLanguage}
      medialink={medialink}
      onClose={handleClose}
      onError={handleError}
      onSessionFinished={onSessionFinished}
      onSessionPaused={onSessionPaused}
      superModule={module}
      tokenAccessor={tokenAccessor}
      translations={getTranslations(t)}
      assignmentId={getAssignmentId(medialink)}
      initialPhase={initialPhase}
      studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
    />
  );
}
